import { useLingui } from "@lingui/react/macro"
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

// Inline-text-edit row. Renders the canonical
// label-on-the-left / value-on-the-right Property row
// with the "warm tint + cream stripe → editor" affordance
// the deal slideout uses (Paper Q1L-1). Used by the
// contact slideover, the desktop contact show page, and
// the mobile contact show panel.
//
// The component owns the visual chrome only — submit /
// optimistic / error logic stays in the caller via the
// `submitting`, `error`, and `onCommit(value)` props.
// `onCommit` is invoked when the user presses Enter or
// clicks the commit button (and only when the trimmed
// value actually changed). The caller decides what to do
// with the raw string (validate, format, build the
// patch + payload, etc.).
//
// `readOnly` flips the row into a non-interactive display
// — used for the WhatsApp phone-pinned case and for
// linked-company rows where the picker (not free text)
// owns the change.

export interface InlineTextEditRowProps {
  label: string
  value: string | null
  readOnly?: boolean
  // Read-state shape selector:
  //
  // - "default" — the value is plain text, the whole
  //   value cluster is the click-to-edit trigger with a
  //   dotted underline (matches the deal slideout
  //   pattern). This is the canonical shape for every
  //   editable row — Title, Phone, Email, LinkedIn,
  //   Website, Company, City, Owner, money, date — and
  //   what callers should reach for by default.
  // - "link" — RARE exception, for rows whose value
  //   MUST render as a real navigable `<a>` (e.g. a
  //   stage badge that links to the pipeline config).
  //   The link is supplied via `readDisplay`; a pencil
  //   button on the right edge of the row is the
  //   dedicated edit trigger — hidden by default on
  //   desktop, revealed on row hover / keyboard focus,
  //   and always visible on mobile (no hover signal to
  //   key off). Do NOT use for plain email / URL
  //   fields — the row is for editing, not for
  //   launching mailto: / opening the profile page.
  //
  // Without `variant="link"`, a `readDisplay` slot is
  // rendered as the value text inside the regular
  // click-to-edit trigger — fine for non-interactive
  // overrides (e.g. the Location row's
  // "city · UTC offset" combined string), broken for
  // anything interactive.
  variant?: "default" | "link"
  // Optional override for the read-state node. With
  // `variant: "link"` this MUST contain the interactive
  // `<a>` (it's rendered directly, outside any trigger
  // button). With the default variant it just replaces
  // the plain text — useful for combined display
  // strings like "Mexico City · UTC−6".
  readDisplay?: ReactNode
  // Forwarded as the HTML `type` attribute on the editor
  // input. Defaults to "text". Use "tel", "email", "url"
  // for the keyboards / autofill hints they unlock.
  inputType?: "text" | "tel" | "email" | "url"
  inputMode?:
    | "text"
    | "tel"
    | "email"
    | "url"
    | "numeric"
    | "decimal"
    | "search"
    | "none"
  placeholder?: string
  // String rendered in the read state when `value` is
  // null/empty. Defaults to an em dash.
  emptyDisplay?: string
  submitting: boolean
  error: string | null
  // Called when the user presses Enter or clicks the
  // commit button. Receives the trimmed input value.
  // Only fires when the new value differs from the
  // current `value` prop.
  onCommit: (rawValue: string) => void
  // Called when the user presses Esc, clicks the cancel
  // button, or commits a no-op. The caller resets its
  // draft / error state.
  onCancel: () => void
  // data-testid prefix for the row's interactive elements.
  // The trigger button gets `${testIdPrefix}-trigger`, the
  // input gets `${testIdPrefix}-input`, the commit button
  // `${testIdPrefix}-commit`, the cancel button
  // `${testIdPrefix}-cancel`, the error message
  // `${testIdPrefix}-error`.
  testIdPrefix?: string
}

export function InlineTextEditRow({
  label,
  value,
  readOnly,
  variant = "default",
  readDisplay,
  inputType = "text",
  inputMode,
  placeholder,
  emptyDisplay = "—",
  submitting,
  error,
  onCommit,
  onCancel,
  testIdPrefix,
}: InlineTextEditRowProps) {
  const { t } = useLingui()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep the draft in sync when the prop changes from the
  // outside (optimistic revert, server reconcile,
  // realtime broadcast). We don't overwrite the user's
  // keystrokes while they're editing — only when the row
  // is idle.
  useEffect(() => {
    if (!editing) {
      setDraft(value ?? "")
    }
  }, [value, editing])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const startEdit = useCallback(() => {
    if (readOnly) return
    setDraft(value ?? "")
    setEditing(true)
  }, [readOnly, value])

  const cancel = useCallback(() => {
    setDraft(value ?? "")
    setEditing(false)
    onCancel()
  }, [onCancel, value])

  const commit = useCallback(() => {
    const trimmed = draft.trim()
    const previous = (value ?? "").trim()
    if (trimmed === previous) {
      // No-op commit — exit without a server round trip.
      setEditing(false)
      onCancel()
      return
    }
    // Keep the editor open while the request is in flight
    // (the input is disabled via `submitting`) and on
    // validation failures (so the user can fix the value
    // without re-clicking the trigger). The `submitting`
    // false → true → false transition below closes the
    // editor on a clean success.
    onCommit(trimmed)
  }, [draft, onCancel, onCommit, value])

  // Auto-close the editor after a successful submit. Two
  // signals: the `submitting` flag transitioned from true
  // to false (the request finished), and there's no
  // `error` to surface. We trust the wrapper to clear
  // `error` to null on success — that's the contract.
  const prevSubmittingRef = useRef(submitting)
  useEffect(() => {
    const justFinished = prevSubmittingRef.current && !submitting
    prevSubmittingRef.current = submitting
    if (justFinished && editing && !error) {
      setEditing(false)
    }
  }, [submitting, editing, error])

  if (!editing) {
    const defaultDisplay =
      value && value.length > 0 ? (
        <ReadValueText text={value} />
      ) : (
        <ReadValueText text={emptyDisplay} muted />
      )

    // Three read-mode shapes:
    //
    // 1. readOnly → static value, no edit affordance.
    // 2. variant: "link" → the value (a real `<a>`
    //    supplied via `readDisplay`) renders directly so
    //    clicking it navigates. A pencil button on the
    //    right edge is the dedicated edit trigger,
    //    hidden until row hover / focus on desktop and
    //    always visible on mobile (no hover signal).
    // 3. default → the whole value cluster is a single
    //    click-to-edit `<button>` with a dotted
    //    underline. Same pattern as the deal slideout
    //    Value / Close date / Owner / Stage rows.
    if (readOnly) {
      return (
        <RowShell label={label}>
          <span
            className="flex items-center gap-1.5 min-w-0"
            data-testid={
              testIdPrefix ? `${testIdPrefix}-readonly` : undefined
            }
          >
            {readDisplay ?? defaultDisplay}
          </span>
        </RowShell>
      )
    }

    if (variant === "link") {
      return (
        <RowShell label={label} hoverGroup>
          <span className="flex items-center gap-1.5 min-w-0">
            {readDisplay ?? defaultDisplay}
          </span>
          <HoverPencilButton
            onClick={startEdit}
            ariaLabel={t`Edit ${label.toLowerCase()}`}
            testId={
              testIdPrefix ? `${testIdPrefix}-trigger` : undefined
            }
          />
        </RowShell>
      )
    }

    return (
      <RowShell label={label}>
        <button
          type="button"
          onClick={startEdit}
          aria-label={t`Edit ${label.toLowerCase()}`}
          data-testid={
            testIdPrefix ? `${testIdPrefix}-trigger` : undefined
          }
          className="flex items-center gap-1.5 min-w-0 cursor-pointer text-left underline-offset-4 [text-decoration:underline_1px_dotted_#C9C2B6] hover:[text-decoration:underline_1px_dotted_#7A746B]"
        >
          {readDisplay ?? defaultDisplay}
        </button>
      </RowShell>
    )
  }

  return (
    <EditingRowShell label={label}>
      <input
        ref={inputRef}
        type={inputType}
        inputMode={inputMode}
        value={draft}
        placeholder={placeholder}
        disabled={submitting}
        aria-label={label}
        aria-invalid={Boolean(error)}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            commit()
          } else if (e.key === "Escape") {
            e.preventDefault()
            e.stopPropagation()
            cancel()
          }
        }}
        data-testid={
          testIdPrefix ? `${testIdPrefix}-input` : undefined
        }
        className="grow shrink basis-0 min-w-0 rounded-md py-1 px-2 bg-white border border-[#B86A2D] text-foreground text-[13px] leading-4 font-medium focus:outline-none [box-shadow:#B86A2D1F_0px_0px_0px_3px]"
      />
      <CommitButton
        onClick={commit}
        disabled={submitting}
        testId={testIdPrefix ? `${testIdPrefix}-commit` : undefined}
      />
      <CancelButton
        onClick={cancel}
        disabled={submitting}
        testId={testIdPrefix ? `${testIdPrefix}-cancel` : undefined}
      />
      {error ? (
        <span
          role="alert"
          className="basis-full text-[#B6675F] text-xs leading-4"
          data-testid={
            testIdPrefix ? `${testIdPrefix}-error` : undefined
          }
        >
          {error}
        </span>
      ) : null}
    </EditingRowShell>
  )
}

function RowShell({
  label,
  children,
  hoverGroup,
}: {
  label: string
  children: ReactNode
  // Mark this row as a CSS `group` so children can react
  // to hover / focus-within at the row level. Used by the
  // link-variant's `HoverPencilButton` to reveal itself
  // when the user moves over the row on desktop.
  hoverGroup?: boolean
}) {
  return (
    <div
      className={`flex items-center py-3 px-4 gap-3 ${
        hoverGroup ? "group" : ""
      }`}
    >
      <span className="grow-0 shrink-0 text-[#7A746B] text-[13px] leading-4">
        {label}
      </span>
      <div className="grow basis-0 min-w-0 flex justify-end items-center gap-1.5">
        {children}
      </div>
    </div>
  )
}

function EditingRowShell({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div
      className="flex flex-wrap items-center py-[9px] pl-3.5 pr-4 gap-x-1.5 gap-y-1 bg-[#FAF7F1] border-l-2 border-l-[#B86A2D]"
      data-state="editing"
    >
      <span className="grow-0 shrink-0 mr-1.5 text-[#7A746B] text-[13px] leading-4">
        {label}
      </span>
      {children}
    </div>
  )
}

function ReadValueText({
  text,
  muted,
}: {
  text: string
  muted?: boolean
}) {
  const tone = muted ? "text-[#7A746B]" : "text-foreground"
  return (
    <span
      className={`text-[13px] leading-4 font-medium truncate min-w-0 ${tone}`}
    >
      {text}
    </span>
  )
}

// Pencil-icon edit trigger for link-typed rows (Email,
// LinkedIn, Website, …). Hidden by default on desktop
// so the value's `<a>` is the dominant affordance — the
// dotted-underline-click-to-edit pattern is reserved
// for plain-text rows where there's no link to compete
// with. Revealed on row hover or keyboard focus-within.
//
// Mobile has no hover signal, so the pencil is always
// visible there. `md:opacity-0` gates the hidden state
// to the desktop breakpoint and up.
function HoverPencilButton({
  onClick,
  ariaLabel,
  testId,
}: {
  onClick: () => void
  ariaLabel: string
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testId}
      className="flex items-center justify-center w-[22px] h-[22px] rounded-md shrink-0 cursor-pointer text-[#7A746B] hover:bg-[#F0EDE6] hover:text-foreground transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 md:focus-visible:opacity-100"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" />
      </svg>
    </button>
  )
}

function CommitButton({
  onClick,
  disabled,
  testId,
}: {
  onClick: () => void
  disabled?: boolean
  testId?: string
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t`Save`}
      data-testid={testId}
      className="flex items-center justify-center w-[26px] h-[26px] rounded-md shrink-0 bg-[#1E1B17] cursor-pointer disabled:opacity-60"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M3 8L6.5 11.5L13 4.5"
          stroke="#F0EDE6"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

function CancelButton({
  onClick,
  disabled,
  testId,
}: {
  onClick: () => void
  disabled?: boolean
  testId?: string
}) {
  const { t } = useLingui()
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t`Cancel`}
      data-testid={testId}
      className="flex items-center justify-center w-[26px] h-[26px] rounded-md shrink-0 bg-white border border-[#C9C2B6] cursor-pointer disabled:opacity-60"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 4L12 12M12 4L4 12"
          stroke="#7A746B"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  )
}
